import {
  Control,
  Path,
  useController,
  UseFormClearErrors,
  UseFormSetError,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { useTranslation } from "@/hooks/useTranslation";
import { useCallback, useRef } from "react";
import type { CheckoutFormData } from "./checkout.types";
import { CartResponse } from "@/networking/carts.api";
import { CountriesAndLanguages } from "@/networking/addresses.api";
import { UseQueryResult } from "@tanstack/react-query";
import RadioInputBar from "./radio-input-bar";
import CreditCardIcons from "./credit-card-icons";
import AlternatePaymentIcons from "./alternate-payment-icons";
import { cn, getPaymentBehavior } from "@/lib/utils";
import { PaymentBehavior } from "@/networking/payments.api";
import { createAddressCountryReset } from "@/lib/address-country-selection";
import { useState, useEffect, useMemo } from "react";
import FluidImage from "../images/fluid-image";
import { FLUID_CDN_URL } from "@/lib/constants";
import CustomerDetailsForm from "./customer-details-form";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";
import VgsCreditCardForm from "./vgs-collect-card-form";
import { VgsFormState } from "@/hooks/useVgsCollect";
import Checkbox from "../ui/input/checkbox";
import AddressForm from "./address-form";
import useCartCountry from "./hooks/useCartCountry";
import useAutocomplete from "./hooks/useAutocomplete";

interface PaymentMethodOption {
  id: number;
  type: string;
  name: string;
  test: boolean;
  sandbox?: boolean;
  token_provider?: string;
  customer_details_schema?: Record<string, unknown>;
  [key: string]: unknown;
}

function Loader() {
  return (
    <div className="mt-2 w-full animate-pulse space-y-4">
      <div className="h-12 w-full rounded bg-gray-200"></div>
      <div className="h-12 w-full rounded bg-gray-200"></div>
      <div className="h-12 w-full rounded bg-gray-200"></div>
    </div>
  );
}

export function TestGatewayTooltip({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="flex flex-row items-center gap-2">
      <div className="flex flex-row items-center gap-1">
        <div className="inline-block h-2 w-2 rounded-full bg-yellow-400" />
        <span className="text-xs text-gray-500">{title}</span>
      </div>
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="mt-0.5 flex h-3 w-3 items-center justify-center rounded-full border border-gray-500 p-0 text-[10px] leading-none font-medium text-gray-500">
              ?
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs font-medium">{description}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

const VGS_SESSION_TIMEOUT = 5 * 60 * 1000;

export default function PaymentMethodSelect({
  control,
  query,
  setValue,
  watch,
  networkError,
  clearErrors,
  countries,
  cartToken,
  paymentMethodsIncludes,
  disabled = false,
  isFormReady,
  iframeLoadError,
  hasActiveVgsSession,
  vgsFieldState,
  resetVgsForm,
  onCustomerDetailsChange,
  customerDetailsValidationErrors,
  onClearCustomerDetailsFieldError,
}: {
  control: Control<CheckoutFormData>;
  query: UseQueryResult<CartResponse | null>;
  setValue: UseFormSetValue<CheckoutFormData>;
  watch: UseFormWatch<CheckoutFormData>;
  networkError?: string;
  setError: UseFormSetError<CheckoutFormData>;
  clearErrors: UseFormClearErrors<CheckoutFormData>;
  countries: CountriesAndLanguages["countries"];
  cartToken: string;
  paymentMethodsIncludes: string | null;
  disabled?: boolean;
  isFormReady?: boolean;
  iframeLoadError?: boolean;
  hasActiveVgsSession?: boolean;
  vgsFieldState?: VgsFormState;
  resetVgsForm?: () => void;
  onCustomerDetailsChange?: (details: Record<string, string> | null) => void;
  customerDetailsValidationErrors?: Record<string, string[]>;
  onClearCustomerDetailsFieldError?: (fieldKey: string) => void;
}) {
  const { t } = useTranslation();
  const { isLoading, data } = query;

  const options = useMemo(() => {
    return data?.cart.available_payment_methods || [];
  }, [data?.cart.available_payment_methods]);

  const cardType = watch("customer.payment_method.card_network");
  const [showAllOptions, setShowAllOptions] = useState(false);
  const [vgsError, setVgsError] = useState<string | undefined>(undefined);

  const useShippingForBilling = watch(
    "customer.payment_method.use_shipping_address_for_billing",
  );
  const shippingCountryCode = watch("customer.shipping_address.country_code");
  const shippingCountryName = watch("customer.shipping_address.country");

  const billingFieldPath =
    "customer.payment_method.billing_address" as Path<CheckoutFormData>;
  const {
    stateOptions: billingStateOptions,
    addressFields: billingAddressFields,
    handleCountryChange: billingHandleCountryChange,
  } = useCartCountry(
    cartToken,
    paymentMethodsIncludes,
    query?.data?.cart?.country?.iso,
    countries,
  );

  const {
    renderAutocompleteOptions: billingRenderAutocompleteOptions,
    getAutocompleteOptions: billingGetAutocompleteOptions,
    handleOptionClick: billingHandleOptionClick,
  } = useAutocomplete(
    setValue,
    `${billingFieldPath}.city` as Path<CheckoutFormData>,
    `${billingFieldPath}.state` as Path<CheckoutFormData>,
    `${billingFieldPath}.postal_code` as Path<CheckoutFormData>,
    `${billingFieldPath}.address1` as Path<CheckoutFormData>,
    billingStateOptions,
  );

  const onBillingCountryChange = useCallback(
    (countryCode: string, countryName: string) => {
      const resetData = createAddressCountryReset(countryCode, countryName);

      Object.entries(resetData).forEach(([key, value]) => {
        setValue(
          `${billingFieldPath}.${key}` as Path<CheckoutFormData>,
          value as string,
        );
      });
      // Don't update cart country
      billingHandleCountryChange(countryCode, false);
    },
    [setValue, billingFieldPath, billingHandleCountryChange],
  );

  const {
    field,
    fieldState: { error },
  } = useController({
    name: "customer.payment_method.type",
    control,
  });
  const selectedPaymentTypeRef = useRef(field.value);

  useEffect(() => {
    selectedPaymentTypeRef.current = field.value;
  }, [field.value]);

  // auto-select first payment method if no selection or current selection is invalid
  useEffect(() => {
    if (options.length > 0) {
      const isCurrentSelectionValid = options.some(
        (method) => method.type === field.value,
      );

      if (!field.value || !isCurrentSelectionValid) {
        const hasCreditCard = options.some(
          (method) => method.type === "credit_card",
        );
        const defaultMethod = hasCreditCard ? "credit_card" : options[0]?.type;

        if (defaultMethod) {
          field.onChange(defaultMethod);
        }
      }
    }
  }, [options, field]);

  const hiddenPaymentOptions = options.slice(4);

  const handlePaymentMethodSelect = async (option: PaymentMethodOption) => {
    const isCreditSelection =
      getPaymentBehavior(option.type) === PaymentBehavior.CREDIT_CARD;

    // Intentionally not calling resetVgsForm() here. VGS's library starts
    // internal 20s iframe-load timers when form.field() is invoked; calling
    // unmount() does NOT cancel them. If we tear down the VGS form (and
    // with it, the iframe host divs) mid-load, VGS's internal timer fires
    // on a detached DOM node and rejects with "Iframe didn't loaded in
    // 20000 ms" — the FLUID-CHECKOUT-Q Sentry noise. Keeping the VGS
    // instance alive across manual payment method switches (combined
    // with the always-mounted form wrapper below) lets its iframes finish
    // loading regardless of which method the user ends up picking.
    //
    // The exception is retrying a known-bad VGS session: if the user reselects
    // credit card after a real iframe load failure, tear down the failed
    // session while the host divs are still mounted so useVgsPayment can
    // initialize a fresh one.
    if (isCreditSelection && iframeLoadError && resetVgsForm) {
      resetVgsForm();
    }

    field.onChange(option.type);
    setVgsError(undefined);

    clearErrors("customer.payment_method");
  };

  // STALE SESSION TIMEOUT — expires the VGS session after VGS_SESSION_TIMEOUT
  // regardless of the currently selected payment method. With the always-mounted
  // form below, the VGS session would otherwise outlive its server-side TTL
  // whenever the user sits on an APM. If CC is still selected at expiry, also
  // clear the field so the user can't submit against a torn-down form.
  useEffect(() => {
    if (!hasActiveVgsSession || !resetVgsForm) {
      return;
    }

    const timer = setTimeout(() => {
      const isCreditSelected =
        getPaymentBehavior(selectedPaymentTypeRef.current) ===
        PaymentBehavior.CREDIT_CARD;

      if (isCreditSelected) {
        setValue("customer.payment_method.type", "");
      }

      resetVgsForm();
    }, VGS_SESSION_TIMEOUT);

    return () => clearTimeout(timer);
  }, [hasActiveVgsSession, resetVgsForm, setValue]);

  const topError = error?.message || networkError || vgsError || undefined;
  const hasNoOptions = !isLoading && options.length === 0;

  // The VGS form is rendered exactly once (under the first credit_card
  // option in the visible list) and kept mounted regardless of the
  // selected payment method. Hiding via CSS instead of unmounting keeps
  // the iframe host divs attached to the DOM so VGS's internal iframe
  // load promises can always resolve, preventing FLUID-CHECKOUT-Q.
  // Assumes a single credit_card behavior option per cart, which matches
  // current hardcoded VGS field IDs (#vgs-card-number, etc.).
  const visibleOptions = options.slice(0, showAllOptions ? options.length : 4);
  const firstCreditIdx = visibleOptions.findIndex(
    (o) => getPaymentBehavior(o.type) === PaymentBehavior.CREDIT_CARD,
  );
  const isAnyCreditSelected =
    getPaymentBehavior(field.value) === PaymentBehavior.CREDIT_CARD;

  // Clear payment method field when no options are available to prevent
  // submitting a stale/invalid payment type
  useEffect(() => {
    if (hasNoOptions && field.value) {
      setValue("customer.payment_method.type", "");
    }
  }, [hasNoOptions, field.value, setValue]);

  if (hasNoOptions) {
    return null;
  }

  return (
    <fieldset className="mt-6">
      <div className="mb-2">
        <div className="flex flex-col justify-between sm:flex-row sm:items-center">
          <div className="text-sm/6 font-medium text-gray-900">
            {t("payment_method")}
          </div>
          <div className="text-xs text-gray-500">
            {t("all_transactions_are_secure_and_encrypted")}
          </div>
        </div>
        {topError && (
          <div
            className="text-sm text-red-500"
            data-testid="payment-select-error-inline"
          >
            {topError}
          </div>
        )}
      </div>

      {isLoading ? (
        <Loader />
      ) : options.length ? (
        <>
          <div className="mt-2 flex flex-col">
            {visibleOptions.map((option, index) => {
              const behavior = getPaymentBehavior(option.type);
              const isCredit = behavior === PaymentBehavior.CREDIT_CARD;
              const isTestMode = isCredit ? option.test : option.sandbox;
              const isSelected = field.value === option.type;
              const isFirst = index === 0;
              const isLast = index === options.length - 1;
              const hasFormFields =
                isCredit ||
                (option.customer_details_schema &&
                  Object.keys(option.customer_details_schema).length > 0) ||
                option.collect_billing_address;
              return (
                <div key={option.id}>
                  <RadioInputBar
                    leftContent={<div>{option.name}</div>}
                    rightContent={
                      <div className="flex flex-row items-center gap-2">
                        {isTestMode ? (
                          <TestGatewayTooltip
                            title={isCredit ? t("test_gateway") : t("sandbox")}
                            description={
                              isCredit
                                ? t("test_gateway_tooltip")
                                : t("sandbox_tooltip")
                            }
                          />
                        ) : null}
                        {isCredit ? (
                          <CreditCardIcons cardType={cardType} />
                        ) : (
                          <AlternatePaymentIcons paymentMethod={option} />
                        )}
                      </div>
                    }
                    name={option.type}
                    isSelected={isSelected}
                    onSelect={() => handlePaymentMethodSelect(option)}
                    disabled={disabled}
                    className={cn(
                      isSelected
                        ? cn(
                            "rounded-b-none bg-white",
                            hasFormFields ? "mt-0.5" : "my-0.5",
                          )
                        : "bg-gray-100/40",
                      isFirst
                        ? "rounded-t"
                        : isLast && !(isSelected && hasFormFields)
                          ? "rounded-b"
                          : "rounded-none",
                      disabled && "cursor-not-allowed opacity-50",
                    )}
                  />
                  {isCredit && index === firstCreditIdx ? (
                    // Keep mounted even when !isAnyCreditSelected so VGS's
                    // iframe host divs stay attached to the DOM. See
                    // handlePaymentMethodSelect above for rationale.
                    <div
                      className={cn(!isAnyCreditSelected && "hidden")}
                      aria-hidden={!isAnyCreditSelected}
                    >
                      <VgsCreditCardForm
                        control={control}
                        setValue={setValue}
                        watch={watch}
                        clearErrors={clearErrors}
                        countries={countries}
                        cartToken={cartToken}
                        paymentMethodsIncludes={paymentMethodsIncludes}
                        query={query}
                        isFormReady={isFormReady || false}
                        iframeLoadError={iframeLoadError}
                        isLast={isLast}
                        vgsFieldState={vgsFieldState || {}}
                        networkError={vgsError}
                      />
                    </div>
                  ) : isSelected &&
                    !isCredit &&
                    ((option.customer_details_schema &&
                      Object.keys(option.customer_details_schema).length > 0) ||
                      option.collect_billing_address) ? (
                    <div>
                      {option.customer_details_schema &&
                      Object.keys(option.customer_details_schema).length > 0 ? (
                        <CustomerDetailsForm
                          paymentMethod={option}
                          onSubmit={(details) =>
                            onCustomerDetailsChange?.(details)
                          }
                          validationErrors={customerDetailsValidationErrors}
                          onClearFieldError={onClearCustomerDetailsFieldError}
                          roundedBottom={
                            isLast && !option.collect_billing_address
                          }
                        />
                      ) : null}
                      {option.collect_billing_address &&
                        !query?.data?.cart?.digital_only && (
                          <>
                            <div
                              className={cn(
                                "h-input flex w-full items-center px-4 ring-1 ring-gray-300",
                                isLast && useShippingForBilling && "rounded-b",
                              )}
                            >
                              <Checkbox
                                control={control}
                                name="customer.payment_method.use_shipping_address_for_billing"
                                label={t("use_shipping_address_for_billing")}
                                id="use-shipping-address-for-billing-alternate"
                                onChange={() => {
                                  const newValue = !useShippingForBilling;
                                  setValue(
                                    "customer.payment_method.use_shipping_address_for_billing",
                                    newValue,
                                  );
                                  clearErrors(
                                    "customer.payment_method.billing_address",
                                  );

                                  // pre-populate billing country from shipping when unchecking
                                  if (!newValue && shippingCountryCode) {
                                    setValue(
                                      `${billingFieldPath}.country_code` as Path<CheckoutFormData>,
                                      shippingCountryCode,
                                    );
                                    setValue(
                                      `${billingFieldPath}.country` as Path<CheckoutFormData>,
                                      shippingCountryName || "",
                                    );
                                    billingHandleCountryChange(
                                      shippingCountryCode,
                                      false,
                                    );
                                  }
                                }}
                              />
                            </div>
                            {!useShippingForBilling && (
                              <AddressForm
                                control={control}
                                countries={countries}
                                watch={watch}
                                fieldPath={billingFieldPath}
                                stateOptions={billingStateOptions}
                                addressFields={billingAddressFields}
                                onCountryChange={onBillingCountryChange}
                                renderAutocompleteOptions={
                                  billingRenderAutocompleteOptions
                                }
                                getAutocompleteOptions={
                                  billingGetAutocompleteOptions
                                }
                                handleOptionClick={billingHandleOptionClick}
                                isLoading={query.isLoading}
                                roundedTop={false}
                                roundedBottom={isLast}
                                className="mt-[-7px] mb-[-7px]"
                                collectName={false}
                                clearErrors={clearErrors}
                              />
                            )}
                          </>
                        )}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
          {hiddenPaymentOptions.length > 0 && !showAllOptions && (
            <div
              className={cn(
                "min-h-input flex w-full items-center justify-between rounded-b-md bg-gray-100/40 p-2 px-4 ring-1 ring-gray-300",
                disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
              )}
              onClick={() => !disabled && setShowAllOptions(true)}
            >
              <div className="flex min-w-0 flex-1 flex-col space-y-1">
                <span className="text-sm">{t("more_payment_methods")}</span>
                <div className="flex flex-row flex-wrap items-center gap-2">
                  {hiddenPaymentOptions.map((option) => (
                    <AlternatePaymentIcons
                      key={option.id}
                      paymentMethod={option}
                    />
                  ))}
                </div>
              </div>
              <FluidImage
                src={`${FLUID_CDN_URL}/images/checkout/icons/chevron-down.svg`}
                alt="Chevron Down"
                width={12}
                height={12}
              />
            </div>
          )}
        </>
      ) : (
        <div />
      )}
    </fieldset>
  );
}
